import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, useCallback } from "react";
import { getOperacionesHoy, marcarCheckin, marcarCheckout, deshacerCheckin, type ReservaOperacion } from "@/lib/operaciones.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogIn, LogOut, BedDouble, Euro, Phone, Mail, Printer, Undo2, Users, Wallet } from "lucide-react";
import { toast } from "sonner";
import { PagosDialog } from "@/components/gestion/pagos/PagosDialog";

export const Route = createFileRoute("/_authenticated/gestion/operaciones")({
  component: OperacionesPage,
});

const fmtEur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmtHora = (iso: string | null) =>
  iso ? new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso)) : "—";

function ReservaCard({ r, tipo, onAction }: { r: ReservaOperacion; tipo: "llegada" | "salida" | "en_casa"; onAction: () => void }) {
  const checkin = useServerFn(marcarCheckin);
  const checkout = useServerFn(marcarCheckout);
  const undo = useServerFn(deshacerCheckin);
  const [loading, setLoading] = useState(false);

  async function handle(fn: () => Promise<unknown>, msg: string) {
    setLoading(true);
    try {
      await fn();
      toast.success(msg);
      onAction();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const yaCheckin = !!r.checkin_at || r.estado === "en_casa" || r.estado === "completada";
  const yaCheckout = !!r.checkout_at || r.estado === "completada";

  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-medium leading-tight">{r.nombre_huesped}</p>
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <BedDouble className="h-3 w-3" /> {r.habitacion_nombre}
              <span className="mx-1">·</span>
              <Users className="h-3 w-3" /> {r.num_huespedes}
              <span className="mx-1">·</span>
              {r.canal}
            </p>
          </div>
          <Badge variant={r.estado === "en_casa" ? "default" : r.estado === "completada" ? "secondary" : "outline"}>
            {r.estado}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.telefono_huesped || "—"}</span>
          <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {r.email_huesped}</span>
          <span>Entrada: {r.fecha_entrada}</span>
          <span>Salida: {r.fecha_salida}</span>
          {yaCheckin && <span>Check-in: {fmtHora(r.checkin_at)}</span>}
          {yaCheckout && <span>Check-out: {fmtHora(r.checkout_at)}</span>}
        </div>

        <div className="flex items-center justify-between rounded-md bg-secondary/50 p-2 text-xs">
          <span className="flex items-center gap-1"><Euro className="h-3 w-3" /> Total: <strong>{fmtEur(r.precio_total)}</strong></span>
          <span>Pagado: {fmtEur(r.pagado)}</span>
          {r.pendiente > 0 && <span className="font-medium text-destructive">Pdte: {fmtEur(r.pendiente)}</span>}
        </div>

        {r.notas && <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">📝 {r.notas}</p>}

        <div className="flex flex-wrap gap-2 print:hidden">
          <PagosDialog
            reservaId={r.id}
            precioTotal={r.precio_total}
            onChange={onAction}
            trigger={<Button size="sm" variant="outline"><Wallet className="mr-1 h-3 w-3" /> Pagos</Button>}
          />
          {tipo === "llegada" && !yaCheckin && (
            <Button size="sm" disabled={loading} onClick={() => handle(() => checkin({ data: { reserva_id: r.id } }), "Check-in registrado")}>
              <LogIn className="mr-1 h-3 w-3" /> Check-in
            </Button>
          )}
          {tipo === "llegada" && yaCheckin && !yaCheckout && (
            <Button size="sm" variant="outline" disabled={loading} onClick={() => handle(() => undo({ data: { reserva_id: r.id } }), "Check-in deshecho")}>
              <Undo2 className="mr-1 h-3 w-3" /> Deshacer
            </Button>
          )}
          {(tipo === "salida" || tipo === "en_casa") && !yaCheckout && (
            <Button size="sm" disabled={loading} onClick={() => handle(() => checkout({ data: { reserva_id: r.id } }), "Check-out registrado")}>
              <LogOut className="mr-1 h-3 w-3" /> Check-out
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function OperacionesPage() {
  const fn = useServerFn(getOperacionesHoy);
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<Awaited<ReturnType<typeof getOperacionesHoy>> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    fn({ data: { fecha } })
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => setErr(e?.message ?? "error"));
  }, [fn, fecha]);

  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-sm text-destructive">{err}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Cargando operaciones…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h2 className="font-serif text-2xl">Operaciones del día</h2>
          <p className="text-sm text-muted-foreground">Llegadas, salidas y huéspedes en casa para el {fecha}.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="rounded-md border bg-background px-3 py-2 text-sm"
          />
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="mr-1 h-4 w-4" /> Imprimir
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <LogIn className="h-4 w-4 text-emerald-600" /> Llegadas ({data.llegadas.length})
              </CardTitle>
            </CardHeader>
          </Card>
          {data.llegadas.length === 0 && <p className="text-xs text-muted-foreground">Sin llegadas previstas.</p>}
          {data.llegadas.map((r) => <ReservaCard key={r.id} r={r} tipo="llegada" onAction={load} />)}
        </section>

        <section className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <BedDouble className="h-4 w-4 text-blue-600" /> En casa ({data.en_casa.length})
              </CardTitle>
            </CardHeader>
          </Card>
          {data.en_casa.length === 0 && <p className="text-xs text-muted-foreground">Nadie alojado hoy.</p>}
          {data.en_casa.map((r) => <ReservaCard key={r.id} r={r} tipo="en_casa" onAction={load} />)}
        </section>

        <section className="space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <LogOut className="h-4 w-4 text-amber-600" /> Salidas ({data.salidas.length})
              </CardTitle>
            </CardHeader>
          </Card>
          {data.salidas.length === 0 && <p className="text-xs text-muted-foreground">Sin salidas previstas.</p>}
          {data.salidas.map((r) => <ReservaCard key={r.id} r={r} tipo="salida" onAction={load} />)}
        </section>
      </div>
    </div>
  );
}